<template>
  <div id="home" class="app">
    <!-- Keep this at the top -->
    <div id="modal-overlay"></div>

    <DoodleView v-if="doodleActive" @closeDoodle="doodleActive = false" />
    <LayoutTopBar @openDoodle="doodleActive = true" />
    <DroneviewerView v-show="showDroneview && hasDroneviewAccess" />
    <FeatureNavigation v-if="!showNoFeature" />
    <UserMessage />
    <MapviewerView
      v-show="showMapview"
      :error="fetchError"
      :initialFetchLoading="initialFetchLoading"
    />
    <IndoorviewerView v-show="isIndoorviewShowing && hasIndoorviewAccess" />
    <ButtonCreatePoi
      v-if="
        isMobileScreen &&
        hasPoiCreatorAccess &&
        ((clickedItemType === 'realEstate' && isMapviewShowing) ||
          (clickedItemType === 'level' && isPlanviewShowing) ||
          (clickedItemType === 'room' && isPlanviewShowing)) &&
        clickedItemType !== 'poi' &&
        !toolboxMenuActive &&
        !isDrawPointActive &&
        (isPlanviewShowing || isMapviewShowing) &&
        !showFeatureAnalytic &&
        !showFeatureProjectAnalysis
      "
      @createPoi="createPoiMobile"
    />
    <ButtonEditPoi
      v-if="
        hasPoiEditorAccess &&
        clickedItemType === 'poi' &&
        (isMapviewShowing || isPlanviewShowing) &&
        !isDrawPointActive &&
        !showFeatureAnalytic &&
        !showFeatureProjectAnalysis
      "
      @showEdit="setShowEditPoi"
    />

    <OverlayModal />
    <OverlayModals />
    <transition name="toast">
      <ToastGeneral v-if="infoMessage" :type="infoMessage.type" :message="infoMessage.message" />
    </transition>
  </div>
  <FeatureComponent v-show="!showNoFeature" feature="home/*" />
</template>

<script>
import DoodleView from '@/components/doodle/DoodleView.vue'
import DroneviewerView from '@/components/droneviewer/DroneviewerView.vue'
import IndoorviewerView from '@/components/indoorviewer/IndoorviewerView.vue'
import LayoutTopBar from '@/design-system/organisms/LayoutTopBar.vue'
import MapviewerView from '@/components/mapviewer/MapviewerView.vue'
import OverlayModal from '@/components/general/OverlayModal.vue'
import ToastGeneral from '@/components/general/overlays/toasts/ToastGeneral.vue'
import FeatureNavigation from '@/design-system/molecules/FeatureNavigation.vue'
import UserMessage from '@/design-system/molecules/UserMessage.vue'
import {
  RESET_CURRENT_TOOL,
  SET_CURRENT_TOOL,
  SET_DRAW_POINT_FEATURE,
  SET_FEATURE_ACCESS,
  SET_MOBILE_SCREEN,
  SET_REALM_ID
} from '@/store/mutation-types'
import { useIndoorviewStore } from '@/pinia/indoorview'
import { useGeneralStore } from '@/pinia'
import { mapState as mapStatePinia, mapActions as mapActionsPinia } from 'pinia'
import { mapGetters, mapMutations, mapState } from 'vuex'
import { useFetchInitialMapData } from '@/hooks/dataFetching/map'
import { getRealmId, shouldShowMovedMessage } from '@/utils/realmId'
import { useRealm } from '@/composables/useRealm'
import { clearLocalStorage } from '@/utils/localstorage'
import { parseJwt } from '@/utils/data'
import { postUserLanguage } from '@/services/realms.service'
import ButtonCreatePoi from '@/design-system/atoms/buttons/ButtonCreatePoi.vue'
import ButtonEditPoi from '@/design-system/atoms/buttons/ButtonEditPoi.vue'
import OverlayModals from '@/design-system/organisms/OverlayModals.vue'
import { toRefs, watch } from 'vue'
import useFeatures, { useFeature } from '@/pinia/features'
import FeatureComponent from '@/components/FeatureComponent.vue'
import components from '@/components'
import { useIotCoverageStore } from '@/pinia/iotCoverage'
import crossIconSource from '@/assets/icon-cross.png'
import measureAreaIconSource from '@/assets/icon-measure-area.svg'
import measureLineIconSource from '@/assets/icon-measure-line.svg'

export default {
  name: 'HomePage',
  components: {
    DoodleView,
    DroneviewerView,
    IndoorviewerView,
    LayoutTopBar,
    MapviewerView,
    OverlayModal,
    OverlayModals,
    ToastGeneral,
    FeatureNavigation,
    ButtonCreatePoi,
    ButtonEditPoi,
    FeatureComponent,
    UserMessage
  },
  setup() {
    const { fetchError, initialFetchLoading, fetchInitialMapData } = useFetchInitialMapData()
    // To trigger onMounted
    useIotCoverageStore()
    const { settings: realmSettings } = toRefs(useRealm())
    const features = useFeatures()
    const { hasFeatureAccess, mutateRootFeature } = features
    const { featurePath } = toRefs(features)
    const { feature } = useFeature('home/*')

    watch(realmSettings, () => {
      // This is a temporary solution to enable/disable the usage feature from the realm settings
      // This can be removed when the features is fetched from the backend
      const enabled = realmSettings.value?.config?.utilization?.enabled
      mutateRootFeature(['home', 'analytics', 'analyticareas', 'usage'], (usage) => ({
        ...usage,
        enabled: enabled !== undefined ? enabled : usage.enabled
      }))
      mutateRootFeature(['home', 'analytics', 'analyticareas', 'usage'], (usage) => ({
        ...usage,
        children: usage.children.map((child) =>
          child.name === 'kpis'
            ? {
                ...child,
                props: {
                  kpis: realmSettings.value.config.utilization.kpis
                }
              }
            : child.name === 'graphs'
            ? {
                ...child,
                props: {
                  graphs: realmSettings.value.config.utilization.graphs
                }
              }
            : child.name === 'heatmaps'
            ? {
                ...child,
                props: {
                  heatmap: realmSettings.value.config.utilization.heatmaps
                }
              }
            : child
        )
      }))
    })

    return {
      fetchError,
      initialFetchLoading,
      realmSettings,
      featurePath,
      fetchInitialMapData,
      hasFeatureAccess,
      feature,
      components
    }
  },
  data() {
    return {
      crossIconSource: crossIconSource,
      doodleActive: false,
      measureAreaIconSource: measureAreaIconSource,
      measureLineIconSource: measureLineIconSource,
      realmId: '',
      showDashboard: false,
      showDroneview: false,
      showMapview: true,
      showPulseloader: false,
      showSearchOverlay: false,
      windowWidth: null
    }
  },
  computed: {
    isMobileScreen() {
      return this.windowWidth <= 1024
    },
    ...mapGetters({
      hasPoiCreatorAccess: 'hasPoiCreatorAccess',
      hasPoiEditorAccess: 'hasPoiEditorAccess',
      hasDroneviewAccess: 'hasDroneviewAccess',
      hasIndoorviewAccess: 'hasIndoorviewAccess',
      isDrawPointActive: 'isDrawPointActive',
      isDroneviewShowing: 'isDroneviewShowing',
      isIndoorviewShowing: 'isIndoorviewShowing',
      isMapviewShowing: 'isMapviewShowing',
      isModelviewShowing: 'isModelviewShowing',
      isPlanviewShowing: 'isPlanviewShowing',
      isDataComparisonToolActive: 'isDataComparisonToolActive'
    }),
    ...mapStatePinia(useIndoorviewStore, [
      'isIndoorviewCropActive',
      'isIndoorviewMeasurementActive'
    ]),
    ...mapStatePinia(useGeneralStore, [
      'showFeatureAnalytic',
      'showFeatureProjectAnalysis',
      'showFeatureErrands',
      'showFeatureInfo',
      'showNoFeature'
    ]),
    ...mapState({
      currentView: (state) => state.currentView,
      currentTool: (state) => state.currentTool,
      menuExpanded: (state) => state.menuExpanded,
      infoMessage: (state) => state.infoMessage,
      buildingSource: (state) => state.buildingSource,
      complexSource: (state) => state.complexSource,
      realEstateSource: (state) => state.realEstateSource,
      clickedItemType: (state) => state.clickedItemType,
      toolboxMenuActive: (state) => state.toolboxMenuActive
    }),
    activeFilterIcon() {
      switch (this.currentTool) {
        case 'datacomparisontool':
          return this.toolsIconSource
        default:
          return this.toolsIconSource
      }
    }
  },
  watch: {
    realmSettings() {
      const realmId = getRealmId()
      const jwt = parseJwt(this.$store.state.oidcStore.access_token)
      if (realmId) {
        let locale = window.appConfig.VITE_I18N_LOCALE
        // Currently only need to get realm if jwt doesn't contain language info
        // The getRealm request could also contain other useful properties in the future
        if (!jwt.language) {
          if (this.realmSettings?.defaultLocale) {
            locale = this.realmSettings.defaultLocale
          }
          // jwt.sub (subject) equals userId
          postUserLanguage(realmId, jwt.sub, locale)
        }
        this.$i18n.locale = jwt.language ? jwt.language : locale
      }
    },
    infoMessage() {
      if (this.infoMessage) setTimeout(() => this.setInfoMessage(null), 4000)
    },
    initialFetchLoading(newVal, oldVal) {
      if (oldVal && !newVal) {
        if (!localStorage.getItem('initialLoadComplete')) {
          localStorage.setItem('initialLoadComplete', true)
        }
      }
    },
    currentView(newView) {
      if (newView === 'mapview' || newView === 'planview' || newView === 'modelview') {
        this.showPulseloader = false
        this.showMapview = true
        this.showDashboard = false
        this.showDroneview = false
      } else if (newView === 'droneview') {
        this.showPulseloader = false
        this.showDroneview = true
        this.showDashboard = false
        this.showMapview = false
      } else {
        this.showPulseloader = false
        this.showDashboard = false
        this.showDroneview = false
        this.showMapview = false
      }
    },
    isMobileScreen() {
      this.setMobileScreen({ mobileScreen: this.isMobileScreen })
    }
  },
  async created() {
    if (
      !this.$store ||
      !this.$store.state ||
      !this.$store.state.oidcStore ||
      !this.$store.state.oidcStore.access_token ||
      !this.$store.state.oidcStore.access_token.length
    ) {
      clearLocalStorage()
      return window.location.replace(window.location.origin + `/oauth2`)
    }
    const jwt = parseJwt(this.$store.state.oidcStore.access_token)
    const featureAccessArray = []
    localStorage.setItem('roles', JSON.stringify(jwt.realm_access?.roles || []))
    jwt.realm_access?.roles.forEach((role) => {
      if (role.startsWith('FEAT_')) featureAccessArray.push(role)
    })
    this.setFeatureAccess(featureAccessArray)
    if (shouldShowMovedMessage()) {
      return this.$router.push('/info')
    }
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (connection?.downlink && connection.downlink < 5)
      this.setInfoMessage({ type: 'warning', message: this.$t('message__slow-internet') })
    const realmId = getRealmId()
    if (realmId) {
      localStorage.setItem('realmId', realmId)
      this.setRealmId({ realmId })
      this.realmId = realmId
    } else {
      this.$router.replace('/realm')
    }
    this.windowWidth = window.innerWidth
    this.setMobileScreen({ mobileScreen: this.isMobileScreen })
    await this.fetchInitialMapData()
    localStorage.setItem('ls.iv_unit_key', '"metric"') // Unofficial way to prevent browser setting from overriding NavVis instance units. Might have to change to other solution in the future.
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth
    })
  },
  methods: {
    ...mapMutations({
      resetTool: RESET_CURRENT_TOOL,
      setDrawPointFeature: SET_DRAW_POINT_FEATURE,
      setFeatureAccess: SET_FEATURE_ACCESS,
      setInfoMessage: 'setInfoMessage',
      setMobileScreen: SET_MOBILE_SCREEN,
      setRealmId: SET_REALM_ID,
      setTool: SET_CURRENT_TOOL
    }),
    createPoiMobile(active) {
      if (active) {
        this.setTool({ tool: 'drawpoint' })
        this.setInfoMessage({
          type: 'warning',
          message: this.$t('message__draw__point-position')
        })
      }
      if (!active) {
        this.resetTool()
      }
    },
    async fetchAllDataFromApi() {
      this.fetchCount = 0
      this.fetchError = null
      this.initialFetchLoading = true
      let buildingSource = null
      let realEstateSource = null
      let complexSource = null
      while ((!complexSource || !realEstateSource || !buildingSource) && this.fetchCount < 5) {
        const fetchPromises = []
        if (!complexSource)
          fetchPromises.push(
            this.loadComplexes().then((res) => {
              complexSource = res
              if (res) this.setComplexSource(res)
            })
          )
        if (!realEstateSource)
          fetchPromises.push(
            this.loadRealEstates().then((res) => {
              realEstateSource = res
              if (res) this.setRealEstateSource(res)
            })
          )
        if (!buildingSource)
          fetchPromises.push(
            this.loadBuildings().then((res) => {
              buildingSource = res
              if (res) this.setBuildingSource(res)
            })
          )
        await Promise.all(fetchPromises)
        this.fetchCount = this.fetchCount + 1
      }
      if (this.fetchCount >= 5)
        this.fetchError = 'Could not fetch data from API. Please reload the application'
      this.initialFetchLoading = false
      localStorage.setItem('ls.iv_unit_key', '"metric"') // Unofficial way to prevent browser setting from overriding NavVis instance units. Might have to change to other solution in the future.
    },
    // goToViewWithResize(view, delay) { // Not used? Might be useful in the future if we need to handle resizing of the map
    //   this.setCurrentView({ view })
    //   if (view !== 'indoorview' && view !== 'droneview') {
    //     const element = document.querySelector('.mapboxgl-canvas')
    //     setTimeout(() => {
    //       if (element.style.width === '400px' && element.style.height === '300px') {
    //         this.$store.map.resize()
    //       }
    //     }, delay) // Need delay so view is set and map is loaded, might have to give it more time
    //   }
    // },
    toggleSearchOverlay() {
      this.showSearchOverlay = !this.showSearchOverlay
    },
    setShowEditPoi(val) {
      this.setCurrentOverlayModalPinia('editPoi')
      this.setShowOverlayModalPinia(val)
    },
    ...mapActionsPinia(useGeneralStore, ['setCurrentOverlayModalPinia', 'setShowOverlayModalPinia'])
  }
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';

.app {
  background-color: variables.$color-greyscale-background;
  height: 100vh;
  width: 100vw;
  overflow: hidden; // Hide horizontal scrollbar when loading the webpage
  scrollbar-width: none; // Disable scrolling when loading the webpage
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media all and (max-width: 1024px) {
  .app {
    background-color: variables.$color-greyscale-background;
    height: 100%;
  }
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(-60px);
}
.toast-enter-active {
  transition: all 0.3s ease;
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(-60px);
}
.toast-leave-active {
  transition: all 0.3s ease;
}
</style>
